// ─────────────────────────────────────────────────────────────────────────────
// Trend Analytics — Predictive trend detection without external AI
// Uses statistical methods: moving average, percentage change, trend direction
// Used by: reportGenerator.js, smartCommandEngine.js
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculate moving average over a data array
 * @param {number[]} data - Array of numeric values
 * @param {number} window - Window size (default: 3)
 * @returns {number[]} Array of moving averages
 */
export const movingAverage = (data, window = 3) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  if (data.length < window) return [data.reduce((a, b) => a + b, 0) / data.length];

  const result = [];
  for (let i = window - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - window + 1; j <= i; j++) {
      sum += data[j];
    }
    result.push(Math.round((sum / window) * 100) / 100);
  }
  return result;
};

/**
 * Calculate percentage change between two values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {number} Percentage change (rounded to 2 decimals)
 */
export const percentageChange = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / Math.abs(previous)) * 10000) / 100;
};

/**
 * Detect trend direction from data array using last N months
 * @param {number[]} data - Array of numeric values (chronological)
 * @param {number} months - Number of recent months to analyze (default: 3)
 * @returns {{ trend: string, confidence_score: number, change: number, details: object }}
 */
export const trendDirection = (data, months = 3) => {
  if (!Array.isArray(data) || data.length < 2) {
    return { trend: 'insufficient_data', confidence_score: 0, change: 0, details: {} };
  }

  const slice = data.slice(-Math.min(months, data.length));
  const ma = movingAverage(slice, Math.min(2, slice.length));

  // Calculate consecutive changes
  const changes = [];
  for (let i = 1; i < slice.length; i++) {
    changes.push(percentageChange(slice[i], slice[i - 1]));
  }

  const avgChange = changes.length > 0
    ? changes.reduce((a, b) => a + b, 0) / changes.length
    : 0;

  // Determine trend direction
  let trend;
  if (avgChange > 5) trend = 'growing';
  else if (avgChange < -5) trend = 'declining';
  else trend = 'stable';

  // Confidence score calculation
  // Higher confidence when all changes point same direction
  const sameDirection = changes.every(c => c > 0) || changes.every(c => c < 0);
  const changeConsistency = sameDirection ? 30 : 0;
  const changeMagnitude = Math.min(Math.abs(avgChange) * 2, 40);
  const dataSufficiency = Math.min((slice.length / months) * 30, 30);
  const confidence_score = Math.min(
    Math.round(changeConsistency + changeMagnitude + dataSufficiency),
    98
  );

  return {
    trend,
    confidence_score: Math.max(confidence_score, 15),
    change: Math.round(avgChange * 100) / 100,
    details: {
      period: slice,
      movingAverages: ma,
      consecutiveChanges: changes,
      dataPoints: slice.length,
    },
  };
};

/**
 * Analyze revenue trend from monthly payment data
 * @param {number[]} monthlyPayments - Array of 12 monthly payment values (Jan-Dec)
 * @returns {object} Revenue trend analysis
 */
export const analyzeRevenueTrend = (monthlyPayments) => {
  if (!Array.isArray(monthlyPayments) || monthlyPayments.length === 0) {
    return { trend: 'insufficient_data', confidence_score: 0 };
  }

  // Get current month index (0-based)
  const currentMonth = new Date().getMonth();
  // Get data up to current month
  const relevantData = monthlyPayments.slice(0, currentMonth + 1);

  const overall = trendDirection(relevantData, 3);
  const lastMonth = relevantData[relevantData.length - 1] || 0;
  const prevMonth = relevantData[relevantData.length - 2] || 0;
  const monthOverMonth = percentageChange(lastMonth, prevMonth);

  // Peak and low months
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const peakIndex = relevantData.indexOf(Math.max(...relevantData));
  const lowIndex = relevantData.indexOf(Math.min(...relevantData));

  return {
    ...overall,
    monthOverMonth,
    peakMonth: { month: monthNames[peakIndex], value: relevantData[peakIndex] },
    lowMonth: { month: monthNames[lowIndex], value: relevantData[lowIndex] },
    totalRevenue: relevantData.reduce((a, b) => a + b, 0),
    averageMonthly: Math.round(relevantData.reduce((a, b) => a + b, 0) / relevantData.length),
  };
};

/**
 * Analyze patient growth trend from monthly member data
 * @param {number[]} monthlyMembers - Array of 12 monthly new member counts
 * @returns {object} Patient growth trend analysis
 */
export const analyzePatientGrowth = (monthlyMembers) => {
  if (!Array.isArray(monthlyMembers) || monthlyMembers.length === 0) {
    return { trend: 'insufficient_data', confidence_score: 0 };
  }

  const currentMonth = new Date().getMonth();
  const relevantData = monthlyMembers.slice(0, currentMonth + 1);

  const overall = trendDirection(relevantData, 3);
  const totalNew = relevantData.reduce((a, b) => a + b, 0);
  const averageMonthly = Math.round(totalNew / relevantData.length);

  return {
    ...overall,
    totalNew,
    averageMonthly,
    lastMonthNew: relevantData[relevantData.length - 1] || 0,
  };
};

/**
 * Detect anomalies in data using simple z-score
 * @param {number[]} data - Array of numeric values
 * @param {number} threshold - Z-score threshold (default: 1.5)
 * @returns {Array<{ index: number, value: number, deviation: string }>}
 */
export const detectAnomalies = (data, threshold = 1.5) => {
  if (!Array.isArray(data) || data.length < 3) return [];

  const mean = data.reduce((a, b) => a + b, 0) / data.length;
  const variance = data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / data.length;
  const stdDev = Math.sqrt(variance);

  if (stdDev === 0) return [];

  return data
    .map((value, index) => {
      const zScore = (value - mean) / stdDev;
      if (Math.abs(zScore) > threshold) {
        return {
          index,
          value,
          deviation: zScore > 0 ? 'high' : 'low',
          zScore: Math.round(zScore * 100) / 100,
        };
      }
      return null;
    })
    .filter(Boolean);
};
